import React, { useMemo } from "react";
import { cn } from "../../lib/utils";

// Modelo definido pelo dono: fundo vivo e sólido, texto escuro e um indicador antes do texto
// (bolinha por padrão, ou quadrado translúcido). A tag neutra mantém texto claro. Cores só pelos
// tokens --badge-*, nunca literal:
// este primitivo aparece em todas as telas.
const variantMap = {
  default: "bg-badge-neutral text-badge-neutral-foreground",
  success: "bg-badge-success text-badge-foreground",
  attention: "bg-badge-attention text-badge-foreground",
  warning: "bg-badge-warning text-badge-foreground",
  critical: "bg-badge-critical text-badge-foreground",
  churn: "bg-badge-churn text-badge-churn-foreground",
  danger: "bg-badge-danger text-badge-foreground",
  info: "bg-badge-info text-badge-foreground",
};

const shapeMap = {
  pill: "rounded-full",
  rounded: "rounded-md",
};

function hexToRgb(hex) {
  const normalized = String(hex || "").trim().replace("#", "");
  if (!/^[\da-f]{3}([\da-f]{3})?$/i.test(normalized)) return null;

  const fullHex = normalized.length === 3
    ? normalized.split("").map((char) => `${char}${char}`).join("")
    : normalized;

  return {
    r: Number.parseInt(fullHex.slice(0, 2), 16),
    g: Number.parseInt(fullHex.slice(2, 4), 16),
    b: Number.parseInt(fullHex.slice(4, 6), 16),
  };
}

function isDarkColor(rgb) {
  const [r, g, b] = [rgb.r, rgb.g, rgb.b].map((value) => {
    const channel = value / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  // Abaixo desse ponto, texto claro contrasta mais que texto escuro.
  return luminance < 0.179;
}

function autoVariant(status, fallback = "default") {
  const text = String(status || "").toLowerCase();
  if (text.includes("inativo") || text.includes("cancel") || text.includes("reprov")) return "danger";
  if (text.includes("ativo") || text.includes("conclu") || text.includes("aprov")) return "success";
  if (text.includes("penden") || text.includes("aguard") || text.includes("andamento")) return "warning";
  if (text.includes("admin")) return "info";
  return fallback;
}

export default function StatusBadge({ status, variant = "default", indicator = "dot", shape = "pill", className = "" }) {
  const finalVariant = autoVariant(status, variant);
  const customRgb = useMemo(
    () => (typeof variant === "string" && variant.startsWith("#") ? hexToRgb(variant) : null),
    [variant]
  );

  // Cor vinda do cadastro: o fundo não muda com o tema, então o texto segue a luminância dela.
  const customClass = customRgb
    ? isDarkColor(customRgb)
      ? "text-background dark:text-foreground"
      : "text-foreground dark:text-background"
    : null;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap px-2.5 py-1 text-xs font-semibold",
        shapeMap[shape] || shapeMap.pill,
        customClass || variantMap[finalVariant] || variantMap.default,
        className
      )}
      style={customRgb ? { backgroundColor: variant } : undefined}
    >
      {indicator === "dot" ? <span className="h-2 w-2 shrink-0 rounded-full bg-current" aria-hidden="true" /> : null}
      {indicator === "square" ? (
        <span className="h-3.5 w-3.5 shrink-0 rounded-[4px] bg-current opacity-30" aria-hidden="true" />
      ) : null}
      {status}
    </span>
  );
}
